import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, Mail, MapPin, Plus, Trash2, X,
  Shield, FileText, Wallet, AlertTriangle, MessageSquare,
} from "lucide-react";

const TABS = [
  { id: "polizas", label: "Pólizas", icon: Shield },
  { id: "contactos", label: "Contactos", icon: MessageSquare },
  { id: "cobros", label: "Cobranza", icon: Wallet },
  { id: "siniestros", label: "Siniestros", icon: AlertTriangle },
];

const TIPOS_CONTACTO = [
  { value: "llamada", label: "Llamada" },
  { value: "email", label: "Email" },
  { value: "reunion", label: "Reunión" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "nota", label: "Nota interna" },
];

export default function AdminComunidadDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("polizas");
  const [contactoModal, setContactoModal] = useState(false);
  const [cobroModal, setCobroModal] = useState(false);
  const [siniestroModal, setSiniestroModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`/admin/comunidades/${id}`);
      setData(data);
    } catch {
      toast.error("Comunidad no encontrada");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  if (loading) return <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>Cargando…</div>;
  if (!data) return <div className="p-10 text-center"><Link to="/admin/comunidades" className="btn-outline">Volver</Link></div>;

  const c = data.cliente;
  const counts = {
    polizas: (c.polizas || []).length,
    contactos: data.contactos.length,
    cobros: data.cobros.length,
    siniestros: data.siniestros.length,
  };

  return (
    <div data-testid="comunidad-detail">
      <Link to="/admin/comunidades" className="text-sm inline-flex items-center gap-1.5 nav-link mb-5" style={{ color: "var(--text-muted)" }}>
        <ArrowLeft size={14}/> Comunidades
      </Link>

      {/* Header card */}
      <div className="bg-white border rounded-xl p-7 mb-6" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="flex flex-col md:flex-row md:items-start gap-4 justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold mb-2" style={{ color: "var(--brand-accent)" }}>Comunidad</div>
            <h1 className="text-3xl font-extrabold tracking-tight mb-2" style={{ fontFamily: "Manrope" }}>{c.nombre}</h1>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm" style={{ color: "var(--text-muted)" }}>
              {c.rut && <span className="font-mono">{c.rut}</span>}
              {c.direccion && <span className="inline-flex items-center gap-1.5"><MapPin size={13}/>{c.direccion}{c.comuna ? `, ${c.comuna}` : ""}</span>}
              {c.email && <span className="inline-flex items-center gap-1.5"><Mail size={13}/>{c.email}</span>}
              {c.telefono && <span className="inline-flex items-center gap-1.5"><Phone size={13}/>{c.telefono}</span>}
            </div>
          </div>
          <span className={`badge badge--${c.estado || "activo"}`}>{c.estado || "activo"}</span>
        </div>
        {(c.contacto_nombre || c.notas) && (
          <div className="mt-5 pt-5 border-t grid md:grid-cols-2 gap-4 text-sm" style={{ borderColor: "var(--border-subtle)" }}>
            {c.contacto_nombre && (
              <div>
                <div className="text-[11px] uppercase tracking-[0.15em] font-bold mb-1" style={{ color: "var(--text-muted)" }}>Contacto principal</div>
                <div className="font-semibold">{c.contacto_nombre}</div>
                {c.contacto_rol && <div className="text-[12px]" style={{ color: "var(--text-muted)" }}>{c.contacto_rol}</div>}
              </div>
            )}
            {c.notas && (
              <div>
                <div className="text-[11px] uppercase tracking-[0.15em] font-bold mb-1" style={{ color: "var(--text-muted)" }}>Notas</div>
                <div>{c.notas}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 transition"
            style={{
              fontFamily: "Manrope",
              color: tab === t.id ? "var(--brand-navy)" : "var(--text-muted)",
              borderColor: tab === t.id ? "var(--brand-accent)" : "transparent",
            }}
            data-testid={`tab-${t.id}`}
          >
            <t.icon size={15}/>
            {t.label}
            <span className="text-[11px] px-1.5 rounded-full" style={{ background: "rgba(10,37,64,0.08)" }}>{counts[t.id]}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="bg-white border rounded-xl" style={{ borderColor: "var(--border-subtle)" }}>
        {tab === "polizas" && <PolizasTab cliente={c} />}
        {tab === "contactos" && <ContactosTab items={data.contactos} onAdd={() => setContactoModal(true)} onChanged={load} />}
        {tab === "cobros" && <CobrosTab items={data.cobros} polizas={c.polizas || []} onAdd={() => setCobroModal(true)} onChanged={load} />}
        {tab === "siniestros" && <SiniestrosTab items={data.siniestros} polizas={c.polizas || []} onAdd={() => setSiniestroModal(true)} onChanged={load} />}
      </div>

      {contactoModal && <ContactoModal clienteId={id} onClose={() => setContactoModal(false)} onCreated={() => { load(); setContactoModal(false); }} />}
      {cobroModal && <CobroModal clienteId={id} polizas={c.polizas || []} onClose={() => setCobroModal(false)} onCreated={() => { load(); setCobroModal(false); }} />}
      {siniestroModal && <SiniestroModal clienteId={id} polizas={c.polizas || []} onClose={() => setSiniestroModal(false)} onCreated={() => { load(); setSiniestroModal(false); }} />}
    </div>
  );
}

// ====== TAB: Pólizas ======
function PolizasTab({ cliente }) {
  const polizas = cliente.polizas || [];
  if (polizas.length === 0) {
    return (
      <div className="p-10 text-center">
        <Shield size={36} className="mx-auto mb-3" style={{ color: "var(--text-muted)" }}/>
        <div className="font-bold mb-1" style={{ fontFamily: "Manrope" }}>Sin pólizas registradas</div>
        <div className="text-sm" style={{ color: "var(--text-muted)" }}>Edita la comunidad desde <Link to="/admin/clientes" className="font-bold" style={{ color: "var(--brand-accent)" }}>Clientes & Pólizas</Link> para agregar.</div>
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="tbl">
        <thead><tr><th>Tipo seguro</th><th>Compañía</th><th>Nº póliza</th><th>Prima UF</th><th>Inicio</th><th>Renovación</th><th>Estado</th></tr></thead>
        <tbody>
          {polizas.map((p, i) => (
            <tr key={p.id || i}>
              <td className="font-semibold capitalize">{p.tipo_seguro?.replace(/_/g, " ")}</td>
              <td>{p.compania}</td>
              <td className="font-mono text-[12px]">{p.numero_poliza}</td>
              <td>UF {Number(p.prima_anual_uf || 0).toFixed(2)}</td>
              <td className="text-[13px]">{p.fecha_inicio}</td>
              <td className="text-[13px]">{p.fecha_renovacion}</td>
              <td><span className={`badge badge--${p.estado}`}>{p.estado}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ====== TAB: Contactos ======
function ContactosTab({ items, onAdd, onChanged }) {
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este contacto?")) return;
    try { await http.delete(`/admin/contactos/${id}`); toast.success("Eliminado"); onChanged(); }
    catch { toast.error("Error"); }
  };

  return (
    <>
      <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
        <h3 className="font-bold" style={{ fontFamily: "Manrope" }}>Histórico de contactos</h3>
        <button className="btn-primary" style={{ padding: "0.5rem 1rem", fontSize: 13 }} onClick={onAdd} data-testid="btn-new-contacto"><Plus size={14}/>Nuevo</button>
      </div>
      {items.length === 0 ? (
        <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>Sin interacciones registradas todavía.</div>
      ) : (
        <div className="divide-y" style={{ borderColor: "var(--border-subtle)" }}>
          {items.map((c) => (
            <div key={c.id} className="p-5 flex gap-4" style={{ borderColor: "var(--border-subtle)" }} data-testid={`contacto-${c.id}`}>
              <div className="w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: "rgba(10,37,64,0.06)" }}>
                <MessageSquare size={16} style={{ color: "var(--brand-navy)" }}/>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(217,119,87,0.15)", color: "var(--brand-accent)" }}>{c.tipo}</span>
                  <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>{c.fecha}</span>
                  {c.ejecutivo && <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>· {c.ejecutivo}</span>}
                </div>
                <div className="font-bold text-[15px] mb-1" style={{ fontFamily: "Manrope" }}>{c.asunto}</div>
                {c.contenido && <div className="text-[14px] whitespace-pre-wrap" style={{ color: "var(--text-muted)" }}>{c.contenido}</div>}
              </div>
              <button onClick={() => remove(c.id)} title="Eliminar" style={{ color: "#D32F2F" }}><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ====== TAB: Cobros ======
function CobrosTab({ items, polizas, onAdd, onChanged }) {
  const updateEstado = async (id, estado) => {
    try { await http.patch(`/admin/cobros/${id}`, { estado }); toast.success("Actualizado"); onChanged(); }
    catch { toast.error("Error"); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este cobro?")) return;
    try { await http.delete(`/admin/cobros/${id}`); toast.success("Eliminado"); onChanged(); }
    catch { toast.error("Error"); }
  };
  const fmt = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);

  return (
    <>
      <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
        <h3 className="font-bold" style={{ fontFamily: "Manrope" }}>Cobros de esta comunidad</h3>
        <button className="btn-primary" style={{ padding: "0.5rem 1rem", fontSize: 13 }} onClick={onAdd} data-testid="btn-new-cobro"><Plus size={14}/>Nuevo cobro</button>
      </div>
      {items.length === 0 ? (
        <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>Sin cobros registrados.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead><tr><th>Descripción</th><th>Cuota</th><th>Monto</th><th>Vence</th><th>Estado</th><th>Pago</th><th></th></tr></thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} data-testid={`cobro-${c.id}`}>
                  <td className="font-semibold">{c.descripcion}</td>
                  <td className="text-[13px]">{c.cuota_n}/{c.cuotas_total}</td>
                  <td>{fmt(c.monto_clp)}</td>
                  <td className="text-[13px]">{c.fecha_vencimiento}</td>
                  <td>
                    <select className="field-input" style={{ padding: "0.3rem 0.5rem", fontSize: 12, width: 120 }} value={c.estado} onChange={(e) => updateEstado(c.id, e.target.value)}>
                      {["pendiente","pagada","vencida","anulada"].map((e) => <option key={e} value={e}>{e}</option>)}
                    </select>
                  </td>
                  <td className="text-[12px]" style={{ color: "var(--text-muted)" }}>
                    {c.fecha_pago || "—"}
                    {c.forma_pago && <div className="text-[11px]">{c.forma_pago}</div>}
                  </td>
                  <td><button onClick={() => remove(c.id)} style={{ color: "#D32F2F" }}><Trash2 size={14}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ====== TAB: Siniestros ======
function SiniestrosTab({ items, polizas, onAdd, onChanged }) {
  const update = async (id, payload) => {
    try { await http.patch(`/admin/siniestros/${id}`, payload); toast.success("Actualizado"); onChanged(); }
    catch { toast.error("Error"); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este siniestro?")) return;
    try { await http.delete(`/admin/siniestros/${id}`); toast.success("Eliminado"); onChanged(); }
    catch { toast.error("Error"); }
  };

  return (
    <>
      <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--border-subtle)" }}>
        <h3 className="font-bold" style={{ fontFamily: "Manrope" }}>Siniestros de esta comunidad</h3>
        <button className="btn-primary" style={{ padding: "0.5rem 1rem", fontSize: 13 }} onClick={onAdd} data-testid="btn-new-siniestro"><Plus size={14}/>Nuevo siniestro</button>
      </div>
      {items.length === 0 ? (
        <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>Sin siniestros registrados.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="tbl">
            <thead><tr><th>Tipo</th><th>Fecha</th><th>Descripción</th><th>Reclamado UF</th><th>Aprobado UF</th><th>Pagado UF</th><th>Estado</th><th></th></tr></thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} data-testid={`siniestro-${s.id}`}>
                  <td className="font-semibold capitalize">{s.tipo_siniestro?.replace(/_/g, " ")}</td>
                  <td className="text-[13px]">{s.fecha_siniestro}</td>
                  <td className="max-w-xs truncate" title={s.descripcion}>{s.descripcion}</td>
                  <td>{Number(s.monto_reclamado_uf || 0).toFixed(1)}</td>
                  <td>{Number(s.monto_aprobado_uf || 0).toFixed(1)}</td>
                  <td className="font-bold" style={{ color: s.monto_pagado_uf ? "#2E7D32" : "var(--text-muted)" }}>{Number(s.monto_pagado_uf || 0).toFixed(1)}</td>
                  <td>
                    <select className="field-input" style={{ padding: "0.3rem 0.5rem", fontSize: 12, width: 120 }} value={s.estado} onChange={(e) => update(s.id, { estado: e.target.value })}>
                      {["reportado","en_proceso","aprobado","rechazado","pagado","cerrado"].map((e) => <option key={e} value={e}>{e}</option>)}
                    </select>
                  </td>
                  <td><button onClick={() => remove(s.id)} style={{ color: "#D32F2F" }}><Trash2 size={14}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ====== Modals ======
function Modal({ title, subtitle, onClose, children, footer, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(10,37,64,0.55)" }} onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white z-10" style={{ borderColor: "var(--border-subtle)" }}>
          <div>
            <h3 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>{title}</h3>
            {subtitle && <div className="text-xs" style={{ color: "var(--text-muted)" }}>{subtitle}</div>}
          </div>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <div className="p-5">{children}</div>
        {footer && <div className="p-5 border-t flex justify-end gap-3 sticky bottom-0 bg-white" style={{ borderColor: "var(--border-subtle)" }}>{footer}</div>}
      </div>
    </div>
  );
}

const Field = ({ label, hint, children }) => (
  <div className="mb-4">
    <label className="field-label">{label}</label>
    {children}
    {hint && <span className="field-hint">{hint}</span>}
  </div>
);

function ContactoModal({ clienteId, onClose, onCreated }) {
  const [form, setForm] = useState({
    tipo: "llamada",
    fecha: new Date().toISOString().slice(0,10),
    asunto: "",
    contenido: "",
    ejecutivo: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      await http.post("/admin/contactos", { cliente_id: clienteId, ...form });
      toast.success("Contacto agregado");
      onCreated();
    } catch { toast.error("Error"); }
  };
  return (
    <Modal title="Nuevo contacto" subtitle="Registra una interacción con la comunidad" onClose={onClose} testid="contacto-modal" footer={
      <>
        <button type="button" className="btn-outline" onClick={onClose}>Cancelar</button>
        <button type="submit" form="contactoForm" className="btn-primary" data-testid="contacto-submit">Guardar</button>
      </>
    }>
      <form id="contactoForm" onSubmit={submit}>
        <Field label="Tipo">
          <select className="field-input" value={form.tipo} onChange={(e) => set("tipo", e.target.value)}>
            {TIPOS_CONTACTO.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Fecha"><input type="date" className="field-input" value={form.fecha} onChange={(e) => set("fecha", e.target.value)} required/></Field>
          <Field label="Ejecutivo"><input className="field-input" value={form.ejecutivo} onChange={(e) => set("ejecutivo", e.target.value)} placeholder="Tu nombre"/></Field>
        </div>
        <Field label="Asunto"><input className="field-input" value={form.asunto} onChange={(e) => set("asunto", e.target.value)} placeholder="Llamada de seguimiento renovación..." required data-testid="contacto-asunto"/></Field>
        <Field label="Contenido / Notas"><textarea rows={4} className="field-input" value={form.contenido} onChange={(e) => set("contenido", e.target.value)}/></Field>
      </form>
    </Modal>
  );
}

function CobroModal({ clienteId, polizas, onClose, onCreated }) {
  const [form, setForm] = useState({
    poliza_id: "",
    descripcion: "",
    monto_clp: "",
    fecha_emision: new Date().toISOString().slice(0,10),
    fecha_vencimiento: "",
    forma_pago: "",
    cuota_n: 1,
    cuotas_total: 1,
    notas: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        cliente_id: clienteId,
        ...form,
        monto_clp: parseFloat(form.monto_clp) || 0,
        cuota_n: parseInt(form.cuota_n) || 1,
        cuotas_total: parseInt(form.cuotas_total) || 1,
        poliza_id: form.poliza_id || null,
      };
      await http.post("/admin/cobros", payload);
      toast.success("Cobro creado");
      onCreated();
    } catch { toast.error("Error"); }
  };
  return (
    <Modal title="Nuevo cobro" subtitle="Cuota o factura asociada a la comunidad" onClose={onClose} testid="cobro-modal" footer={
      <>
        <button type="button" className="btn-outline" onClick={onClose}>Cancelar</button>
        <button type="submit" form="cobroForm" className="btn-primary" data-testid="cobro-submit">Crear</button>
      </>
    }>
      <form id="cobroForm" onSubmit={submit}>
        {polizas.length > 0 && (
          <Field label="Póliza asociada (opcional)">
            <select className="field-input" value={form.poliza_id} onChange={(e) => set("poliza_id", e.target.value)}>
              <option value="">— Sin póliza —</option>
              {polizas.map((p) => <option key={p.id} value={p.id}>{p.tipo_seguro?.replace(/_/g," ")} · {p.compania} · {p.numero_poliza}</option>)}
            </select>
          </Field>
        )}
        <Field label="Descripción"><input className="field-input" value={form.descripcion} onChange={(e) => set("descripcion", e.target.value)} placeholder="Cuota 1/4 — Incendio + Sismo" required data-testid="cobro-descripcion"/></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Monto CLP"><input type="number" min="0" className="field-input" value={form.monto_clp} onChange={(e) => set("monto_clp", e.target.value)} required/></Field>
          <Field label="Forma de pago">
            <select className="field-input" value={form.forma_pago} onChange={(e) => set("forma_pago", e.target.value)}>
              <option value="">—</option>
              <option>Transferencia</option><option>PAT</option><option>PAC</option><option>Cheque</option><option>Efectivo</option>
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Fecha emisión"><input type="date" className="field-input" value={form.fecha_emision} onChange={(e) => set("fecha_emision", e.target.value)} required/></Field>
          <Field label="Fecha vencimiento"><input type="date" className="field-input" value={form.fecha_vencimiento} onChange={(e) => set("fecha_vencimiento", e.target.value)} required/></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Cuota N°"><input type="number" min="1" className="field-input" value={form.cuota_n} onChange={(e) => set("cuota_n", e.target.value)}/></Field>
          <Field label="Total cuotas"><input type="number" min="1" className="field-input" value={form.cuotas_total} onChange={(e) => set("cuotas_total", e.target.value)}/></Field>
        </div>
      </form>
    </Modal>
  );
}

function SiniestroModal({ clienteId, polizas, onClose, onCreated }) {
  const [form, setForm] = useState({
    poliza_id: "",
    fecha_siniestro: new Date().toISOString().slice(0,10),
    tipo_siniestro: "incendio",
    descripcion: "",
    ubicacion: "",
    monto_reclamado_uf: "",
    aseguradora: "",
    ejecutivo_asignado: "",
    notas: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        cliente_id: clienteId,
        ...form,
        monto_reclamado_uf: parseFloat(form.monto_reclamado_uf) || 0,
        poliza_id: form.poliza_id || null,
      };
      await http.post("/admin/siniestros", payload);
      toast.success("Siniestro registrado");
      onCreated();
    } catch { toast.error("Error"); }
  };
  return (
    <Modal title="Nuevo siniestro" subtitle="Reporta un evento que afecta a la comunidad" onClose={onClose} testid="siniestro-modal" footer={
      <>
        <button type="button" className="btn-outline" onClick={onClose}>Cancelar</button>
        <button type="submit" form="siniestroForm" className="btn-primary" data-testid="siniestro-submit">Registrar</button>
      </>
    }>
      <form id="siniestroForm" onSubmit={submit}>
        {polizas.length > 0 && (
          <Field label="Póliza afectada (opcional)">
            <select className="field-input" value={form.poliza_id} onChange={(e) => set("poliza_id", e.target.value)}>
              <option value="">— Sin póliza —</option>
              {polizas.map((p) => <option key={p.id} value={p.id}>{p.tipo_seguro?.replace(/_/g," ")} · {p.compania} · {p.numero_poliza}</option>)}
            </select>
          </Field>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Fecha siniestro"><input type="date" className="field-input" value={form.fecha_siniestro} onChange={(e) => set("fecha_siniestro", e.target.value)} required/></Field>
          <Field label="Tipo">
            <select className="field-input" value={form.tipo_siniestro} onChange={(e) => set("tipo_siniestro", e.target.value)} required data-testid="siniestro-tipo">
              <option value="incendio">Incendio</option>
              <option value="sismo">Sismo</option>
              <option value="robo">Robo</option>
              <option value="dano_agua">Daño por agua</option>
              <option value="vehiculo">Vehículo</option>
              <option value="lesion">Lesión</option>
              <option value="responsabilidad_civil">Responsabilidad civil</option>
              <option value="otro">Otro</option>
            </select>
          </Field>
        </div>
        <Field label="Descripción"><textarea rows={3} className="field-input" value={form.descripcion} onChange={(e) => set("descripcion", e.target.value)} required data-testid="siniestro-descripcion"/></Field>
        <Field label="Ubicación"><input className="field-input" value={form.ubicacion} onChange={(e) => set("ubicacion", e.target.value)} placeholder="Ej. departamento 401, sala caldera, estacionamiento..."/></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Monto reclamado UF"><input type="number" step="0.1" min="0" className="field-input" value={form.monto_reclamado_uf} onChange={(e) => set("monto_reclamado_uf", e.target.value)}/></Field>
          <Field label="Aseguradora"><input className="field-input" value={form.aseguradora} onChange={(e) => set("aseguradora", e.target.value)} placeholder="BCI, Chubb..."/></Field>
        </div>
        <Field label="Ejecutivo asignado"><input className="field-input" value={form.ejecutivo_asignado} onChange={(e) => set("ejecutivo_asignado", e.target.value)}/></Field>
      </form>
    </Modal>
  );
}
